import type { ResourceConfig } from '../interfaces/ResourceConfig';

/**
 * @group Functions
 * @summary <blockquote>Merges two ResourceConfig objects with proper deep merging.</blockquote>
 *
 * @param baseConfig - The base ResourceConfig to merge into
 * @param overrideConfig - The ResourceConfig to merge on top (takes precedence)
 * @returns A new merged ResourceConfig object
 *
 * @example
 * ```typescript
 * import { mergeResourceConfig } from '@shared/config';
 * 
 * const merged = mergeResourceConfig(baseConfig, overrideConfig);
 * ```
 */
export function mergeResourceConfig(
  baseConfig: ResourceConfig,
  overrideConfig: Partial<ResourceConfig>
): ResourceConfig {
  return {
    ...baseConfig,
    ...overrideConfig,
    // Deep merge auth
    auth: {
      ...(baseConfig.auth || {}),
      ...(overrideConfig.auth || {}),
    } as ResourceConfig['auth'],
    // Deep merge reporting
    reporting: overrideConfig.reporting
      ? {
          ...(baseConfig.reporting || {}),
          ...overrideConfig.reporting,
          // Deep merge each environment
          development: overrideConfig.reporting.development
            ? {
                ...(baseConfig.reporting?.development || {}),
                ...overrideConfig.reporting.development,
              }
            : baseConfig.reporting?.development,
          preview: overrideConfig.reporting.preview
            ? {
                ...(baseConfig.reporting?.preview || {}),
                ...overrideConfig.reporting.preview,
              }
            : baseConfig.reporting?.preview,
          production: overrideConfig.reporting.production
            ? {
                ...(baseConfig.reporting?.production || {}),
                ...overrideConfig.reporting.production,
              }
            : baseConfig.reporting?.production,
        }
      : baseConfig.reporting,
    // Deep merge logs
    logs: overrideConfig.logs
      ? {
          ...(baseConfig.logs || {}),
          ...overrideConfig.logs,
          // Deep merge each environment
          development: overrideConfig.logs.development
            ? {
                ...(baseConfig.logs?.development || {}),
                ...overrideConfig.logs.development,
              }
            : baseConfig.logs?.development,
          preview: overrideConfig.logs.preview
            ? {
                ...(baseConfig.logs?.preview || {}),
                ...overrideConfig.logs.preview,
              }
            : baseConfig.logs?.preview,
          production: overrideConfig.logs.production
            ? {
                ...(baseConfig.logs?.production || {}),
                ...overrideConfig.logs.production,
              }
            : baseConfig.logs?.production,
        }
      : baseConfig.logs,
  };
}

